import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader } from "@/components/ui-helpers";
import { FolderKanban, CheckSquare, PhoneCall, ArrowRightLeft } from "lucide-react";

const Section = ({ icon: Icon, title, count, children }) => (
  <div>
    <div className="flex items-center gap-2 mb-2"><Icon className="h-4 w-4 text-primary" /><h4 className="font-head font-bold text-slate-900 text-sm">{title}</h4><span className="text-xs text-slate-400">({count})</span></div>
    {count === 0 ? <div className="text-xs text-slate-400 px-1 pb-2">Nothing here.</div> : children}
  </div>
);

export function WorkloadDrilldown({ person, team, onClose, onChanged, startReassign = false }) {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState([]);
  const free = team.filter((r) => r.id !== person.id).sort((a, b) => (a.open_cases - b.open_cases) || ((a.role === "admin") - (b.role === "admin")));
  const [to, setTo] = useState(free[0]?.id || "");
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/dashboard/staff-workload/${person.id}`).then((r) => {
    setData(r.data);
    if (startReassign) setSel(r.data.cases.slice(0, Math.max(1, Math.ceil(r.data.cases.length / 3))).map((c) => c.id));
  }).catch(() => setData({ cases: [], tasks: [], followups: [] }));
  useEffect(() => { load(); }, [person.id]); // eslint-disable-line

  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const move = async () => {
    const target = team.find((r) => r.id === to);
    if (!window.confirm(`Move ${sel.length} case${sel.length === 1 ? "" : "s"} from ${person.name} to ${target?.name}?`)) return;
    setBusy(true);
    try {
      const { data: r } = await api.post("/applications/bulk-reassign", { ids: sel, from_id: person.id, to_id: to });
      toast.success(`${r.moved} case${r.moved === 1 ? "" : "s"} moved to ${target?.name}`);
      setSel([]); load(); onChanged();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="workload-drilldown">
        <DialogHeader><DialogTitle>{person.name}'s workload</DialogTitle></DialogHeader>
        {!data ? <Loader /> : (
          <div className="space-y-5">
            <Section icon={FolderKanban} title="Open cases" count={data.cases.length}>
              <div className="rounded-xl border border-slate-100 divide-y divide-slate-50">
                {data.cases.map((c) => (
                  <label key={c.id} data-testid={`drill-case-${c.id}`} className={`flex items-center gap-3 px-3 py-2 text-sm cursor-pointer ${sel.includes(c.id) ? "bg-primary/5" : "hover:bg-slate-50"}`}>
                    <input type="checkbox" data-testid={`drill-case-select-${c.id}`} checked={sel.includes(c.id)} onChange={() => toggle(c.id)} className="h-4 w-4 accent-primary shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium text-slate-800">{c.client_name} <span className="text-slate-400 font-normal">· {c.service_type}</span></div>
                      <div className="text-[11px] text-slate-400 truncate">{c.stage || "—"} · as {c.roles.join(" & ")}</div>
                    </div>
                    <Link to={`/admin/applications/${c.id}`} onClick={(e) => e.stopPropagation()} className="text-xs font-semibold text-primary shrink-0">Open</Link>
                  </label>
                ))}
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl bg-slate-50 p-3" data-testid="drill-reassign-bar">
                <ArrowRightLeft className="h-4 w-4 text-slate-500" />
                <span className="text-sm text-slate-600">Move {sel.length} selected to</span>
                <select data-testid="drill-reassign-to" value={to} onChange={(e) => setTo(e.target.value)} className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-sm">
                  {free.map((r) => <option key={r.id} value={r.id}>{r.name} ({r.open_cases} open)</option>)}
                </select>
                <button data-testid="drill-reassign-submit" disabled={!sel.length || !to || busy} onClick={move} className="ml-auto bg-primary text-white px-4 py-1.5 rounded-lg text-sm font-semibold disabled:opacity-50">{busy ? "Moving…" : "Move cases"}</button>
              </div>
            </Section>
            <Section icon={CheckSquare} title="Open tasks" count={data.tasks.length}>
              <div className="rounded-xl border border-slate-100 divide-y divide-slate-50">
                {data.tasks.map((t) => (
                  <Link key={t.id} to={t.application_id ? `/admin/applications/${t.application_id}?tab=tasks` : "/admin/tasks"} className="flex items-center justify-between gap-3 px-3 py-2 text-sm hover:bg-slate-50">
                    <span className="truncate text-slate-800">{t.title} <span className="text-[11px] text-slate-400">· Created by {t.created_by_name || "—"}</span></span>
                    <span className={`text-xs shrink-0 ${t.overdue ? "text-rose-600 font-semibold" : "text-slate-400"}`}>{t.due_date ? `${t.overdue ? "Overdue · " : "Due "}${t.due_date.slice(0, 10)}` : "No due date"}</span>
                  </Link>
                ))}
              </div>
            </Section>
            <Section icon={PhoneCall} title="Follow-ups due" count={data.followups.length}>
              <div className="rounded-xl border border-slate-100 divide-y divide-slate-50">
                {data.followups.map((l) => (
                  <Link key={l.id} to={`/admin/leads?lead=${l.id}`} className="flex items-center justify-between gap-3 px-3 py-2 text-sm hover:bg-slate-50">
                    <span className="truncate text-slate-800">{l.name} <span className="text-slate-400">· {l.status}</span></span>
                    <span className="text-xs text-amber-600 shrink-0">{(l.next_followup || "").slice(0, 10)}</span>
                  </Link>
                ))}
              </div>
            </Section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
